/**
 * Campi della pratica per ogni portale di caricamento: le sezioni e i campi sono quelli del modulo del portale
 * (deploy/<portale>/campi-processi.md), con l'obbligatorieta' del portale. Le chiavi sono condivise tra i portali
 * quando il dato e' lo stesso (anagrafica, documento, indirizzi, pagamento, consensi), cosi' l'anagrafica del cliente
 * e i documenti letti le precompilano allo stesso modo; i caricamenti automatici (Teamwork, 1Mobile) le leggono da qui.
 */
export type Values = Record<string, string>
/** PRIVATO o AZIENDA, dalla scelta unica fatta accanto all'operatore. */
type Cond = (v: Values, tipo: string) => boolean

export type ProfileOption = { value: string; label: string }
export type ProfileField = {
  key: string
  label: string
  type: 'text' | 'select' | 'radio' | 'checkbox'
  options?: ProfileOption[]
  inputType?: string
  maxLength?: number
  hint?: string
  /** Obbligatorio per il portale (sempre o a condizione). */
  required?: boolean | Cond
  /** Mostrato solo se la condizione vale. */
  when?: Cond
  /**
   * Valore proposto quando si compila la scheda «Dati per il portale» e il campo è vuoto (es. la data di oggi): si vede e
   * si può cambiare prima di salvare. Non si applica alla creazione della pratica (vedi dynamicProfileDefaults).
   */
  defaultValue?: () => string
}
export type ProfileSection = { title: string; description?: string; when?: Cond; fields: ProfileField[] }
export interface PortalProfile {
  /** Nome del portale e del modulo, mostrato sopra le sezioni. */
  portal: string
  sections: ProfileSection[]
  /** Valori proposti finche' la pratica non ne ha uno; diventano dati della pratica al salvataggio. */
  defaults?: Values
}

// --- costruttori ---------------------------------------------------------------------------------------------------
const o = (...values: (string | [string, string])[]): ProfileOption[] =>
  values.map(v => (Array.isArray(v) ? { value: v[0], label: v[1] } : { value: v, label: v }))
const text = (key: string, label: string, extra: Partial<ProfileField> = {}): ProfileField => ({ key, label, type: 'text', ...extra })
const select = (key: string, label: string, options: ProfileOption[], extra: Partial<ProfileField> = {}): ProfileField =>
  ({ key, label, type: 'select', options, ...extra })
const radio = (key: string, label: string, options: ProfileOption[], extra: Partial<ProfileField> = {}): ProfileField =>
  ({ key, label, type: 'radio', options, ...extra })
const check = (key: string, label: string, extra: Partial<ProfileField> = {}): ProfileField => ({ key, label, type: 'checkbox', ...extra })
const R = { required: true } as const
const SI_NO = o(['SI', 'Sì'], ['NO', 'No'])

const privato: Cond = (_v, t) => t !== 'AZIENDA'
const azienda: Cond = (_v, t) => t === 'AZIENDA'
const flag = (k: string): Cond => v => v[k] === 'SI'
const is = (k: string, ...vals: string[]): Cond => v => vals.includes(v[k] ?? '')
const and = (...cs: Cond[]): Cond => (v, t) => cs.every(c => c(v, t))

const FORME_GIURIDICHE = o('DITTA IND.', 'LIBERO PROFESSIONISTA', 'S.N.C.', 'S.A.S.', 'S.R.L.', 'S.R.L.S.', 'S.P.A.', 'S.A.P.A.',
  'S.S.', 'S.C.', 'S.C.P.A.', 'S.C.R.L.', 'SOC.COOP.AGR.', 'CONSORZIO', 'CONDOMINIO', 'ASSOCIAZIONE', 'ONLUS', 'ENTE', 'AMM. PUBB.', 'ALTRO')

/** Titolare (privato) o azienda con legale rappresentante. */
function intestatario(opts: { forma?: boolean; pec?: boolean | 'azienda'; aziende?: boolean; nomeSoloPrivato?: boolean } = {}): ProfileField[] {
  const aziende = opts.aziende ?? true
  // Teamwork chiede nome, cognome e CF solo ai residenziali; Coraly sempre (per le aziende sono del legale rappresentante).
  const persona = opts.nomeSoloPrivato ? { required: privato } : R
  return [
    ...(aziende ? [
      text('RAGIONE_SOCIALE_CLIENTE', 'Ragione sociale', { when: azienda, required: azienda }),
      text('PARTITA_IVA_CLIENTE', 'Partita IVA', { maxLength: 11, when: azienda, required: azienda }),
      ...(opts.forma ? [select('FORMA_GIURIDICA', 'Forma giuridica', FORME_GIURIDICHE, { when: azienda, required: azienda })] : []),
      ...(opts.pec ? [text('EMAIL_PEC_CLIENTE', 'PEC', { inputType: 'email', when: azienda, required: azienda })] : []),
    ] : []),
    text('NOME_CLIENTE', 'Nome', persona),
    text('COGNOME_CLIENTE', 'Cognome', persona),
    text('CODICE_FISCALE_CLIENTE', 'Codice fiscale', { maxLength: 16, ...persona }),
  ]
}
const sesso = (req = true, extra: ProfileOption[] = []) => radio('SESSO', 'Sesso', [...o(['M', 'Maschio'], ['F', 'Femmina']), ...extra], { required: req })
const nascita = (req: boolean | Cond = true) => [
  text('DATA_NASCITA', 'Data di nascita', { hint: 'gg/mm/aaaa', required: req }),
  text('LUOGO_NASCITA', 'Luogo di nascita', { required: req }),
]
const DOC_TIPI = o(['CI', "Carta d'identità"], ['CIE', "Carta d'identità elettronica"], ['PATENTE', 'Patente'], ['PASSAPORTO', 'Passaporto'])
const ENTI = o(['COMUNE', 'Comune'], ['MOTORIZZAZIONE', 'Motorizzazione'], ['QUESTURA', 'Questura'])
function documento(opts: { req?: boolean; scadenza?: boolean; luogo?: boolean; cittadinanza?: boolean; tipi?: ProfileOption[] } = {}): ProfileField[] {
  const req = opts.req ?? true
  return [
    select('DOC_TIPO', 'Tipo documento', opts.tipi ?? DOC_TIPI, { required: req }),
    text('DOC_NUMERO', 'Numero documento', { maxLength: 20, required: req }),
    text('DOC_RILASCIO', 'Data di rilascio', { hint: 'gg/mm/aaaa', required: req }),
    text('DOC_SCADENZA', 'Data di scadenza', { hint: 'gg/mm/aaaa', required: req && (opts.scadenza ?? true) }),
    select('DOC_ENTE', 'Ente di rilascio', ENTI, { required: req }),
    ...(opts.luogo ? [text('DOC_LUOGO_RILASCIO', 'Luogo di rilascio', R)] : []),
    ...(opts.cittadinanza ? [text('NAZIONALITA', 'Cittadinanza', R)] : []),
  ]
}
/** Indirizzo in campi separati (via, civico, comune, provincia, CAP); `s` = suffisso delle chiavi. */
function indirizzo(s: 'CLIENTE' | 'ATTIVAZIONE', req: boolean | Cond = true, civicoReq: boolean | Cond = req): ProfileField[] {
  return [
    text(`INDIRIZZO_${s}`, 'Via / piazza', { required: req }),
    text(`NUM_CIVICO_${s}`, 'Civico', { required: civicoReq }),
    text(s === 'CLIENTE' ? 'ID_COMUNE_CLIENTE' : 'COMUNE_ATTIVAZIONE', 'Comune', { required: req }),
    text(`PROVINCIA_${s}`, 'Provincia', { maxLength: 2, required: req }),
    text(`CAP_${s}`, 'CAP', { maxLength: 5, required: req }),
  ]
}
function contatti(opts: { mobileReq?: boolean; emailReq?: boolean; alternativo?: boolean } = {}): ProfileField[] {
  return [
    text('CELLULARE_CLIENTE', 'Cellulare', { inputType: 'tel', required: opts.mobileReq ?? true }),
    text('TEL_CLIENTE', 'Telefono', { inputType: 'tel' }),
    ...(opts.alternativo ? [text('RECAPITO_ALTERNATIVO', 'Recapito alternativo', { inputType: 'tel' })] : []),
    text('EMAIL_SPED', 'Email', { inputType: 'email', required: opts.emailReq ?? false }),
  ]
}
const fatturazione = (opts: { cartacea?: boolean; indirizzoReq?: boolean } = {}): ProfileSection => ({
  title: 'Fatturazione',
  fields: [
    radio('FATTURAZIONE', 'Fattura', opts.cartacea === false ? o(['ELETTRONICA', 'Elettronica']) : o(['ELETTRONICA', 'Elettronica'], ['CARTACEA', 'Cartacea']), R),
    text('EMAIL_FATTURAZIONE', 'Email per la fattura', { inputType: 'email', ...R }),
    text('INDIRIZZO_FATTURAZIONE', 'Indirizzo di fatturazione (se diverso)', { required: opts.indirizzoReq ?? false }),
  ],
})
const METODI: Record<string, string> = { SDD: 'RID (addebito su conto)', CARTA: 'Carta di credito', BOLLETTINO: 'Bollettino postale', BONIFICO: 'Bonifico' }
/** Pagamento: RID con IBAN e pagatore (diverso dall'intestatario se spuntato). */
function pagamento(metodi: string[], opts: { req?: boolean; pagatoreSempre?: boolean } = {}): ProfileSection {
  const rid = is('MODO_PAGAMENTO', 'SDD')
  const pagatore: Cond = opts.pagatoreSempre ? rid : and(rid, flag('PAGATORE_DIVERSO'))
  return {
    title: 'Pagamento',
    fields: [
      radio('MODO_PAGAMENTO', 'Metodo di pagamento', metodi.map(m => ({ value: m, label: METODI[m] ?? m })), { required: opts.req ?? true }),
      text('IBAN', 'IBAN', { maxLength: 34, when: rid, required: rid }),
      ...(opts.pagatoreSempre ? [] : [check('PAGATORE_DIVERSO', "Intestatario dell'IBAN diverso dal cliente", { when: rid })]),
      text('SDD_NOME_FIRMATARIO', 'Nome pagatore', { when: pagatore, required: pagatore }),
      text('SDD_COGNOME_FIRMATARIO', 'Cognome pagatore', { when: pagatore, required: pagatore }),
      text('SDD_CF_FIRMATARIO', 'Codice fiscale pagatore', { maxLength: 16, when: pagatore, required: pagatore }),
    ],
  }
}
const CONSENSI: Record<string, string> = {
  CONSENSO_PRIVACY: 'Consenso privacy', CONSENSO_MARKETING: 'Consenso marketing', CONSENSO_PROFILAZIONE: 'Consenso profilazione',
  CONSENSO_TERZI: 'Consenso cessione a terzi', CONSENSO_RICONTATTO: 'Consenso ricontatto',
  AUTORIZZAZIONE_RIPENSAMENTO: 'Attivazione anticipata (prima dei 14 giorni di ripensamento)',
}
function consensi(keys: string[], opts: { req?: boolean; codice?: boolean; vendita?: boolean } = {}): ProfileSection {
  return {
    title: 'Consensi e vendita',
    fields: [
      ...keys.map(k => radio(k, CONSENSI[k] ?? k, SI_NO, { required: opts.req ?? true })),
      ...(opts.codice ? [text('CODICE_CONSENSO', 'Codice consenso')] : []),
      ...(opts.vendita ? [radio('MODALITA_VENDITA', 'Modalità di vendita', o(['TELEFONO', 'Telefono'], ['FISICO', 'Di persona']), R)] : []),
    ],
  }
}
/** Delega a un familiare (TIM, Enel): dati e documento del delegato. */
function delega(tipologie: string[]): ProfileSection {
  const d = flag('DELEGA')
  return {
    title: 'Delega',
    fields: [
      check('DELEGA', 'Firma un delegato'),
      select('DELEGATO_TIPOLOGIA', 'Tipologia delegato', o(...tipologie), { when: d, required: d }),
      text('DELEGATO_NOME', 'Nome delegato', { when: d, required: d }),
      text('DELEGATO_COGNOME', 'Cognome delegato', { when: d, required: d }),
      text('DELEGATO_CF', 'Codice fiscale delegato', { maxLength: 16, when: d, required: d }),
      text('DELEGATO_DATA_NASCITA', 'Data di nascita delegato', { hint: 'gg/mm/aaaa', when: d }),
      text('DELEGATO_TELEFONO', 'Telefono delegato', { inputType: 'tel', when: d, required: d }),
      select('DELEGATO_DOC_TIPO', 'Documento delegato', DOC_TIPI, { when: d, required: d }),
      text('DELEGATO_DOC_NUMERO', 'Numero documento delegato', { when: d, required: d }),
      select('DELEGATO_DOC_ENTE', 'Ente di rilascio', ENTI, { when: d, required: d }),
      text('DELEGATO_DOC_RILASCIO', 'Data di rilascio', { hint: 'gg/mm/aaaa', when: d }),
    ],
  }
}
const fornitura = (opts: { regioni?: string[]; regione?: boolean; civicoReq?: boolean } = {}): ProfileSection => ({
  title: 'Fornitura',
  description: 'Dove arriva la fornitura (luce o gas).',
  fields: [
    ...(opts.regioni ? [select('REGIONE_FORNITURA', 'Regione fornitura', o(...opts.regioni), R)]
      : opts.regione ? [text('REGIONE_FORNITURA', 'Regione fornitura', R)] : []),
    ...indirizzo('ATTIVAZIONE', true, opts.civicoReq ?? true),
  ],
})

// --- telefonia: attivazione della linea e portabilita' ---------------------------------------------------------------
const portaNumero: Cond = v => ['PORTABILITA', 'MIGRAZIONE'].includes(v['TIPO_ATTIVAZIONE'] ?? '')
function lineaFissa(opts: { tecnologie?: string[]; mobile?: boolean } = {}): ProfileSection {
  const fisso: Cond = v => v['TIPO_LINEA'] !== 'MOBILE'
  const mobile: Cond = v => v['TIPO_LINEA'] === 'MOBILE'
  return {
    title: 'Linea',
    description: 'Dove si attiva la linea e cosa si porta dal vecchio operatore.',
    fields: [
      ...(opts.mobile ? [radio('TIPO_LINEA', 'Tipologia', o(['FISSO', 'Fisso'], ['MOBILE', 'Mobile']), R)] : []),
      radio('TIPO_ATTIVAZIONE', 'Attivazione', o(['NUOVA', 'Nuova linea'], ['MIGRAZIONE', 'Cambio operatore (migrazione)'], ['PORTABILITA', 'Portabilità del numero']), R),
      ...(opts.tecnologie ? [select('TECNOLOGIA', 'Tecnologia', o(...opts.tecnologie), { when: fisso, required: fisso })] : []),
      ...indirizzo('ATTIVAZIONE', fisso).map(f => ({ ...f, when: fisso })),
      text('NUMERO_DA_PORTARE', 'Numero da portare', { inputType: 'tel', when: portaNumero, required: portaNumero }),
      text('OPERATORE_ATTUALE', 'Operatore attuale', { when: portaNumero, required: portaNumero }),
      text('CODICE_MIGRAZIONE', 'Codice di migrazione', { when: and(portaNumero, fisso), required: is('TIPO_ATTIVAZIONE', 'MIGRAZIONE') }),
      ...(opts.mobile ? [
        radio('MEMBERSHIP_SIM', 'SIM attuale', o(['RICARICABILE', 'Ricaricabile'], ['ABBONAMENTO', 'Abbonamento']), { when: and(mobile, portaNumero) }),
        text('SERIALE_SIM', 'Seriale SIM (ICCID)', { maxLength: 20, when: mobile }),
      ] : []),
    ],
  }
}

// --- Sky: cosa si attiva e dati del prodotto --------------------------------------------------------------------------
// Su Coraly («Sales Process - Sky Residential») il modulo iniziale non ha campi del prodotto: compaiono dopo la scelta
// del prodotto, dal suo modello nel database «Prodotti Sky | Residential» (riletto il 30/10/2026). Per TV, Sky Wifi e
// opzioni il gruppo e' unico (attivazione fisso linea attiva/non attiva, copertura FTTC/FTTH, numero fisso da migrare,
// gestore, codice di migrazione, intestatario fisso diverso con documento, tecnologia TV Stream/Q, pacchetti aggiuntivi,
// Brand Key, codice dealer, data inserimento brand, ID promo); Sky Mobile ha il suo (attivazione NUOVA/MNP, Giga, numero,
// gestore, seriali SIM, intestatario SIM diverso); Sky Wifi Business Promo aggiunge «Spot». Qui gli stessi campi, con le
// stesse voci, mostrati in base a cosa si attiva cosi' chi carica li copia dalla pratica.
export const SKY_TIPOLOGIE = o(['TV', 'Sky TV / Sky Glass'], ['FISSO', 'Sky Wifi (linea fissa)'], ['TV_FISSO', 'Sky TV + Sky Wifi'],
  ['MOBILE', 'Sky Mobile'], ['OPZIONE', 'Opzione su un abbonamento esistente'])
const skyTv: Cond = v => ['TV', 'TV_FISSO'].includes(v['SKY_TIPOLOGIA'] ?? '')
const skyFisso: Cond = v => ['FISSO', 'TV_FISSO'].includes(v['SKY_TIPOLOGIA'] ?? '')
const skyMobile: Cond = v => v['SKY_TIPOLOGIA'] === 'MOBILE'
const skyOpzione: Cond = v => v['SKY_TIPOLOGIA'] === 'OPZIONE'
const skyNonMobile: Cond = v => !!v['SKY_TIPOLOGIA'] && v['SKY_TIPOLOGIA'] !== 'MOBILE'
const skyPacchetti: Cond = v => ['TV', 'TV_FISSO', 'OPZIONE'].includes(v['SKY_TIPOLOGIA'] ?? '')
const skyPortaFisso = and(skyFisso, is('TIPO_ATTIVAZIONE', 'MIGRAZIONE'))
const skyPortaMobile = and(skyMobile, is('TIPO_ATTIVAZIONE', 'PORTABILITA'))
const skyFissoAltrui = and(skyPortaFisso, flag('FISSO_INTESTATARIO_DIVERSO'))
const skySimAltrui = and(skyPortaMobile, flag('SIM_INTESTATARIO_DIVERSO'))
const SKY_DOC_TIPI = o(['CI', "Carta d'identità"], ['PATENTE', 'Patente di guida'], ['PASSAPORTO', 'Passaporto'])
/**
 * Rete di copertura trovata su sky.it (31/10/2026): non è un campo di Coraly, ma il canvass PROMO SKY ha ID promo diversi
 * per Open Fiber e FW-Fibercop, quindi decide quali promo proporre. La verifica resta manuale (pagina Sky o My Sales): la
 * pagina Sky usa un'API privata del sito che il CRM non chiama.
 */
export const SKY_RETI = o(['OPEN_FIBER', 'Open Fiber'], ['FW_FIBERCOP', 'FW-Fibercop'], ['NON_COPERTO', 'Non coperto'])
/** Data di oggi in Italia come gg/mm/aaaa. */
const oggi = () => new Intl.DateTimeFormat('it-IT', { timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date())
/** Pacchetti aggiuntivi del portale (etichette con il prezzo come su Coraly). */
export const SKY_PACCHETTI: [string, string][] = [
  ['PACCHETTO_SKY_SPORT', 'Sky Sport (22,90 €)'], ['PACCHETTO_SKY_CINEMA', 'Sky Cinema (10 €)'], ['PACCHETTO_SKY_CALCIO', 'Sky Calcio (8 €)'],
  ['PACCHETTO_SKY_KIDS', 'Sky Kids (5 €)'], ['PACCHETTO_SKY_ULTRA_HD', 'Sky Ultra HD 4K (5 €)'], ['PACCHETTO_NETFLIX_BASE', 'Netflix Base (5 €)'],
  ['PACCHETTO_NETFLIX_STANDARD', 'Netflix Standard (9 €)'], ['PACCHETTO_NETFLIX_PREMIUM', 'Netflix Premium (13 €)'],
]
function prodottoSky(): ProfileSection {
  return {
    title: 'Prodotto e linea',
    description: 'I campi che Coraly aggiunge dopo la scelta del prodotto: linea fissa, TV e pacchetti, oppure numero e SIM per Sky Mobile.',
    fields: [
      radio('SKY_TIPOLOGIA', 'Cosa si attiva', SKY_TIPOLOGIE, R),
      text('CODICE_CLIENTE_SKY', 'Codice cliente Sky', { when: skyOpzione, hint: "L'abbonamento esistente a cui si aggiunge l'opzione (non è sul portale: serve a chi carica)." }),
      // Linea fissa (Sky Wifi)
      radio('TIPO_ATTIVAZIONE', 'Tipologia attivazione fisso', o(['NUOVA', 'Linea non attiva (nuova linea)'], ['MIGRAZIONE', 'Linea attiva (migrazione, mantiene il numero)']),
        { when: skyFisso, required: skyFisso }),
      select('SKY_COPERTURA_RETE', 'Rete di copertura', SKY_RETI, { when: skyFisso, required: skyFisso,
        hint: "Dalla verifica su sky.it all'indirizzo di attivazione: decide quali ID promo valgono." }),
      select('TECNOLOGIA', 'Copertura', o('FTTC', 'FTTH'), { when: skyFisso, required: skyFisso, hint: 'Tecnologia della linea trovata dalla verifica (campo del portale).' }),
      text('NUMERO_DA_PORTARE', 'Numero fisso da migrare', { inputType: 'tel', when: skyPortaFisso, required: skyPortaFisso }),
      text('OPERATORE_ATTUALE', 'Gestore attuale', { when: skyPortaFisso, required: skyPortaFisso }),
      text('CODICE_MIGRAZIONE', 'Codice di migrazione', { maxLength: 20, when: skyPortaFisso, required: skyPortaFisso, hint: 'In bolletta del vecchio operatore (18-19 caratteri).' }),
      check('FISSO_INTESTATARIO_DIVERSO', 'Intestatario della linea fissa diverso dal cliente', { when: skyPortaFisso }),
      text('FISSO_INTESTATARIO_NOME', 'Nome intestatario fisso', { when: skyFissoAltrui, required: skyFissoAltrui }),
      text('FISSO_INTESTATARIO_COGNOME', 'Cognome intestatario fisso', { when: skyFissoAltrui, required: skyFissoAltrui }),
      text('FISSO_INTESTATARIO_CF', 'Codice fiscale intestatario fisso', { maxLength: 16, when: skyFissoAltrui, required: skyFissoAltrui }),
      select('FISSO_INTESTATARIO_DOC_TIPO', 'Tipo documento intestatario fisso', SKY_DOC_TIPI, { when: skyFissoAltrui }),
      text('FISSO_INTESTATARIO_DOC_NUMERO', 'Numero documento intestatario fisso', { maxLength: 20, when: skyFissoAltrui }),
      text('FISSO_INTESTATARIO_DOC_RILASCIO', 'Data rilascio documento intestatario fisso', { hint: 'gg/mm/aaaa', when: skyFissoAltrui }),
      text('FISSO_INTESTATARIO_DOC_SCADENZA', 'Data scadenza documento intestatario fisso', { hint: 'gg/mm/aaaa', when: skyFissoAltrui }),
      select('FISSO_INTESTATARIO_DOC_ENTE', 'Ente rilascio documento intestatario fisso', ENTI, { when: skyFissoAltrui }),
      check('SKY_SPOT', 'Spot', { when: and(azienda, skyFisso), hint: 'Campo del prodotto Sky Wifi Business Promo.' }),
      // TV
      select('TECNOLOGIA_TV', 'Tecnologia TV', o(['STREAM', 'Stream (via internet, anche Sky Glass)'], ['Q', 'Q (decoder Sky Q)']), { when: skyTv, required: skyTv }),
      ...SKY_PACCHETTI.map(([key, label]) => check(key, label, { when: skyPacchetti, hint: 'Pacchetto aggiuntivo' })),
      // Sky Mobile
      radio('TIPO_ATTIVAZIONE', 'Tipologia attivazione mobile', o(['NUOVA', 'Nuovo numero (NUOVA)'], ['PORTABILITA', 'Portabilità del numero (MNP)']),
        { when: skyMobile, required: skyMobile }),
      select('SKY_MOBILE_GIGA', 'Giga', o(['150GB', 'Sky Mobile 150 GB (8,95 €)'], ['200GB', 'Sky Mobile 200 GB (10,95 €)'], ['300GB', 'Sky Mobile 300 GB (12,95 €)']),
        { when: skyMobile, required: skyMobile }),
      text('NUMERO_DA_PORTARE', 'Numero telefono mobile da portare', { inputType: 'tel', when: skyPortaMobile, required: skyPortaMobile }),
      text('OPERATORE_ATTUALE', 'Gestore attuale mobile', { when: skyPortaMobile, required: skyPortaMobile }),
      text('SERIALE_SIM', 'Seriale SIM attuale (donating)', { maxLength: 20, when: skyPortaMobile, required: skyPortaMobile, hint: 'ICCID: 19-20 cifre sul retro della SIM da portare.' }),
      text('SERIALE_SIM_SKY', 'Seriale SIM Sky', { maxLength: 20, when: skyMobile, hint: 'Se la SIM Sky è già stata consegnata al cliente.' }),
      check('SIM_INTESTATARIO_DIVERSO', 'Intestatario della SIM diverso dal cliente', { when: skyPortaMobile }),
      text('SIM_INTESTATARIO_NOME', 'Nome intestatario SIM', { when: skySimAltrui, required: skySimAltrui }),
      text('SIM_INTESTATARIO_COGNOME', 'Cognome intestatario SIM', { when: skySimAltrui, required: skySimAltrui }),
      text('SIM_INTESTATARIO_CF', 'Codice fiscale intestatario SIM', { maxLength: 16, when: skySimAltrui, required: skySimAltrui }),
    ],
  }
}
/**
 * Codici del prodotto che il back office compila all'inserimento su Sky (non per Sky Mobile). L'ID promo si sceglie tra
 * quelli del canvass PROMO SKY letto dal server, filtrati per cosa si attiva, tipo di cliente e rete di copertura
 * (lib/skyPromo.ts); la data di inserimento brand si propone a oggi quando si compila la scheda.
 */
function codiciSky(): ProfileSection {
  return {
    title: 'Codici Sky',
    description: 'Campi del prodotto sul portale compilati di solito dal back office: lasciali vuoti se non li conosci.',
    when: skyNonMobile,
    fields: [
      text('SKY_ID_PROMO', 'ID Promo', { hint: 'Scegli la promo del canvass in corso: le condizioni dell\'offerta compaiono sotto.' }),
      text('SKY_BRAND_KEY', 'Brand Key'),
      text('SKY_CODICE_DEALER', 'Codice dealer'),
      text('SKY_DATA_INSERIMENTO_BRAND', 'Data inserimento brand', { hint: 'gg/mm/aaaa (proposta: oggi)', defaultValue: oggi }),
    ],
  }
}

/** Tipologia Sky proposta dal nome del prodotto (database Coraly: TV, FISSO, TV + FISSO, MOBILE, OPZIONE). */
export function skyTipologiaDaProdotto(nome: string | undefined): string | undefined {
  const n = (nome ?? '').toUpperCase()
  if (!n.trim()) return undefined
  if (/MOBILE/.test(n)) return 'MOBILE'
  const wifi = /WI-?FI|FISSO|TRIPLE/.test(n)
  const tv = /\bTV\b|TRIPLE|GLASS|PROVA SKY|SKY BAR|UFFICI|SPORT|CINEMA|CALCIO|KIDS|NETFLIX|RECONNECT/.test(n)
  if (wifi) return tv ? 'TV_FISSO' : 'FISSO'
  if (/UPSELL|OPZION|PARCO/.test(n)) return 'OPZIONE'
  return 'TV'
}

// --- profili per portale -------------------------------------------------------------------------------------------

/** 1Mobile (Effortel CIM): SIM ricaricabile; passo 2 anagrafica con documento, passo 3 SIM e portabilita'. */
export const PROFILE_1MOBILE: PortalProfile = {
  portal: 'Portale 1Mobile',
  defaults: { TIPO_SIM: 'FISICA', TRASFERIMENTO_CREDITO: 'SI', PORTABILITA_PARZIALE: 'NO', TIPO_CONTRATTO_ATTUALE: 'PREPAGATO' },
  sections: [
    {
      title: 'Intestatario', description: "Dati e documento del titolare (per un'azienda, del legale rappresentante).",
      fields: [
        text('RAGIONE_SOCIALE_CLIENTE', 'Ragione sociale', { when: azienda, required: azienda }),
        text('PARTITA_IVA_CLIENTE', 'Partita IVA', { maxLength: 11, when: azienda, required: azienda }),
        text('LEGALE_RAPPRESENTANTE_CLIENTE', 'Legale rappresentante', { when: azienda, required: azienda }),
        text('NOME_CLIENTE', 'Nome', { when: privato, required: privato }),
        text('COGNOME_CLIENTE', 'Cognome', { when: privato, required: privato }),
        text('CODICE_FISCALE_CLIENTE', 'Codice fiscale', { maxLength: 16, required: privato }),
        sesso(), ...nascita(), text('NAZIONALITA', 'Nazionalità', R),
        ...documento(),
        ...indirizzo('CLIENTE', true),
        text('EMAIL_SPED', 'Email', { inputType: 'email', ...R }), text('TEL_CLIENTE', 'Altro telefono', { inputType: 'tel' }),
      ],
    },
    {
      title: 'SIM', description: 'Nuovo numero o portabilità, e la SIM da attivare.',
      fields: [
        radio('TIPO_ATTIVAZIONE', 'Numero', o(['NUOVA', 'Nuovo numero'], ['PORTABILITA', 'Portabilità del numero']), R),
        radio('TIPO_SIM', 'Tipo di SIM', o(['FISICA', 'SIM fisica'], ['ESIM', 'eSIM'])),
        text('SERIALE_SIM', 'Seriale SIM (ICCID)', { maxLength: 20, hint: '19 cifre, comprese le prime 893910', when: is('TIPO_SIM', 'FISICA'), required: is('TIPO_SIM', 'FISICA') }),
        text('NUMERO_DA_PORTARE', 'Numero da portare', { inputType: 'tel', when: is('TIPO_ATTIVAZIONE', 'PORTABILITA'), required: is('TIPO_ATTIVAZIONE', 'PORTABILITA') }),
        text('OPERATORE_ATTUALE', 'Operatore attuale', { when: is('TIPO_ATTIVAZIONE', 'PORTABILITA'), required: is('TIPO_ATTIVAZIONE', 'PORTABILITA') }),
        radio('TIPO_CONTRATTO_ATTUALE', 'Contratto attuale', o(['PREPAGATO', 'Ricaricabile'], ['ABBONAMENTO', 'Abbonamento']), { when: is('TIPO_ATTIVAZIONE', 'PORTABILITA') }),
        radio('TRASFERIMENTO_CREDITO', 'Trasferimento del credito', SI_NO, { when: is('TIPO_ATTIVAZIONE', 'PORTABILITA') }),
        radio('PORTABILITA_PARZIALE', 'Portabilità parziale', SI_NO, { when: is('TIPO_ATTIVAZIONE', 'PORTABILITA') }),
      ],
    },
    { title: 'Consensi', fields: [radio('CONSENSO_PRIVACY', 'Consenso privacy', SI_NO, R)] },
  ],
}

/** Teamwork, scheda comune + Fastweb (bsc 263 residenziale, 270 business). Chiavi lette da deploy/teamwork/carica.mjs. */
export const PROFILE_FASTWEB: PortalProfile = {
  portal: 'Teamwork · Fastweb',
  sections: [
    {
      title: 'Intestatario',
      fields: [
        ...intestatario({ nomeSoloPrivato: true }),
        text('LEGALE_RAPPRESENTANTE_CLIENTE', 'Legale rappresentante', { when: azienda }),
        ...nascita(privato),
        ...indirizzo('CLIENTE', true),
        text('TEL_CLIENTE', 'Telefono', { inputType: 'tel' }), text('CELLULARE_CLIENTE', 'Cellulare', { inputType: 'tel' }),
        text('EMAIL_SPED', 'Email', { inputType: 'email' }),
        ...documento({ req: false }),
      ],
    },
    lineaFissa({ mobile: true, tecnologie: ['GPON', 'VULA', 'SEMI_VULA', 'FTTN', 'FTTH', 'BS NGA', 'BS GPON', 'FWA', 'ADSL ULL', 'ADSL WHS', 'SHDSL'] }),
    {
      title: 'Offerta', fields: [
        check('ENERGIA_ABBINATA', 'Abbinata a Fastweb Energy (coperture "+ ENERGY")'),
        // Fisso business: la copertura Teamwork è FISSO[ VOUCHER]_[NON ]CONVERGENTE. Convergente vale anche da sola se
        // la pratica ha SIM collegate.
        check('CONVERGENTE', 'Convergente (fisso con SIM Fastweb)', { when: azienda }),
        check('VOUCHER', 'Con voucher', { when: azienda }),      ],
    },
    { ...pagamento(['SDD', 'CARTA'], { req: false }), description: 'Facoltativo sul portale.' },
    { title: 'Consensi', fields: [text('CODICE_CONSENSO', 'Codice consenso', { maxLength: 15 })] },
  ],
}

/** Teamwork, altri brand telefonia (Wind3, Eolo, Vodafone Business): scheda comune e linea. */
const teamworkTelco = (portal: string, opts: { mobileReq?: boolean; nascita?: boolean; documento?: boolean } = {}): PortalProfile => ({
  portal,
  sections: [
    {
      title: 'Intestatario',
      fields: [
        ...intestatario({ nomeSoloPrivato: true }),
        ...(opts.nascita === false ? [] : nascita(privato)),
        ...indirizzo('CLIENTE', true),
        text('TEL_CLIENTE', 'Telefono', { inputType: 'tel' }),
        text('CELLULARE_CLIENTE', 'Cellulare', { inputType: 'tel', required: opts.mobileReq ?? false }),
        text('EMAIL_SPED', 'Email', { inputType: 'email' }),
        ...(opts.documento === false ? [] : documento({ req: false })),
      ],
    },
    lineaFissa({ mobile: true }),
    { ...pagamento(['SDD', 'BOLLETTINO', 'CARTA', 'BONIFICO'], { req: false }), description: 'Facoltativo sul portale.' },
    { title: 'Consensi', fields: [text('CODICE_CONSENSO', 'Codice consenso', { maxLength: 15 })] },
  ],
})
export const PROFILE_WIND3 = teamworkTelco('Teamwork · Wind3', { mobileReq: true })
export const PROFILE_EOLO = teamworkTelco('Teamwork · Eolo')
export const PROFILE_VODAFONE = teamworkTelco('Teamwork · Vodafone Business', { nascita: false, documento: false })

/** Coraly, TIM Digital: anagrafica con documento, attivazione, pagamento, consensi, delega. */
export const PROFILE_TIM: PortalProfile = {
  portal: 'Coraly · TIM',
  sections: [
    {
      title: 'Intestatario',
      fields: [...intestatario({ aziende: false }), ...nascita(), ...indirizzo('CLIENTE', true),
        ...contatti({ mobileReq: true, emailReq: true, alternativo: true }), ...documento()],
    },
    lineaFissa({ mobile: true }),
    {
      title: 'Spedizione e fatturazione',
      fields: [
        check('SPEDIZIONE_DIVERSA', 'Indirizzo di spedizione diverso'),
        text('INDIRIZZO_SPEDIZIONE', 'Indirizzo di spedizione', { when: flag('SPEDIZIONE_DIVERSA'), required: flag('SPEDIZIONE_DIVERSA') }),
        check('FATTURAZIONE_DIVERSA', 'Indirizzo di fatturazione diverso'),
        text('INDIRIZZO_FATTURAZIONE', 'Indirizzo di fatturazione', { when: flag('FATTURAZIONE_DIVERSA'), required: flag('FATTURAZIONE_DIVERSA') }),
      ],
    },
    pagamento(['BOLLETTINO', 'SDD'], { pagatoreSempre: true }),
    {
      title: 'Consensi e vendita',
      fields: [
        radio('CONSENSO_MARKETING', 'Consenso marketing', SI_NO), radio('CONSENSO_PROFILAZIONE', 'Consenso profilazione', SI_NO),
        radio('CONSENSO_TERZI', 'Consenso cessione a terzi', SI_NO),
        text('TEL_CONSENSO_PRIVACY', 'Telefono del consenso privacy', { inputType: 'tel', ...R }),
        text('DATA_CONSENSO_PRIVACY', 'Data del consenso privacy', { hint: 'gg/mm/aaaa' }),
        text('CODICE_CONSENSO', 'Codice consenso'),
        radio('MODALITA_VENDITA', 'Modalità di vendita', o(['TELEFONO', 'Telefono'], ['FISICO', 'Di persona']), R),
      ],
    },
    delega(['Marito/Moglie', 'Convivente', 'Genitore', 'Figlio', 'Parente']),
  ],
}

/**
 * Coraly, Sky Residential: cliente, documento, residenza, consegna, attivazione, fattura, pagamento, consensi; in piu'
 * (30/10/2026) i campi che il portale aggiunge dopo la scelta del prodotto (linea fissa, TV e pacchetti, Sky Mobile, codici).
 */
export const PROFILE_SKY: PortalProfile = {
  portal: 'Coraly · Sky',
  sections: [
    {
      title: 'Intestatario',
      fields: [...intestatario({ forma: true, pec: 'azienda' }), sesso(), ...nascita(false), ...documento({ luogo: true, cittadinanza: true }),
        ...indirizzo('CLIENTE', true), text('TEL_CLIENTE', 'Telefono', { inputType: 'tel' }),
        text('CELLULARE_CLIENTE', 'Cellulare', { inputType: 'tel', ...R }), text('EMAIL_SPED', 'Email', { inputType: 'email', ...R })],
    },
    prodottoSky(),
    codiciSky(),
    {
      title: 'Consegna e attivazione', description: "Solo se diversi dalla residenza. Per Sky Wifi l'indirizzo di attivazione è dove si installa la linea.",
      fields: [
        check('SPEDIZIONE_DIVERSA', 'Indirizzo di consegna diverso dalla residenza'),
        text('INDIRIZZO_SPEDIZIONE', 'Indirizzo di consegna (via, civico, comune, CAP, provincia)', { when: flag('SPEDIZIONE_DIVERSA'), required: flag('SPEDIZIONE_DIVERSA') }),
        check('ATTIVAZIONE_DIVERSA', 'Indirizzo di attivazione diverso dalla consegna'),
        ...indirizzo('ATTIVAZIONE', false).map(f => ({ ...f, when: flag('ATTIVAZIONE_DIVERSA') })),
      ],
    },
    { title: 'Fatturazione', fields: [radio('FATTURAZIONE', 'Fattura', o(['ELETTRONICA', 'Elettronica'], ['CARTACEA', 'Cartacea']), R)] },
    pagamento(['CARTA', 'SDD']),
    consensi(['CONSENSO_RICONTATTO', 'CONSENSO_MARKETING', 'CONSENSO_TERZI'], { vendita: true }),
  ],
}

/** Coraly, Retelit Agent Business: solo aziende. */
export const PROFILE_RETELIT: PortalProfile = {
  portal: 'Coraly · Retelit',
  sections: [
    {
      title: 'Azienda e legale rappresentante',
      fields: [
        text('RAGIONE_SOCIALE_CLIENTE', 'Ragione sociale', R), select('FORMA_GIURIDICA', 'Forma giuridica', FORME_GIURIDICHE, R),
        text('PARTITA_IVA_CLIENTE', 'Partita IVA', { maxLength: 11, ...R }),
        text('NOME_CLIENTE', 'Nome', R), text('COGNOME_CLIENTE', 'Cognome', R), sesso(true, o(['NB', 'Non binario'])),
        text('CODICE_FISCALE_CLIENTE', 'Codice fiscale', { maxLength: 16, ...R }), ...nascita(),
        ...documento({ luogo: true, cittadinanza: true }),
        text('TEL_CLIENTE', 'Telefono', { inputType: 'tel' }), text('CELLULARE_CLIENTE', 'Cellulare', { inputType: 'tel', ...R }),
        text('EMAIL_SPED', 'Email', { inputType: 'email' }),
      ],
    },
    { title: 'Sede legale', fields: indirizzo('CLIENTE', true, false) },
    { title: 'Sede di fornitura', fields: indirizzo('ATTIVAZIONE', true, false) },
    fatturazione(),
    pagamento(['SDD', 'CARTA']),
    consensi(['CONSENSO_PROFILAZIONE', 'CONSENSO_MARKETING', 'CONSENSO_TERZI'], { codice: true }),
  ],
}

/** Coraly, "Startform Energia" (Acea, A2A, Enega): niente POD/PDR, che il portale chiede dopo. */
function coralyEnergia(portal: string, opts: { regioni?: string[]; regione?: boolean; aziende?: boolean; forma?: boolean;
  metodi: string[]; sessoReq?: boolean; emailReq?: boolean; indirizzoFatturaReq?: boolean; cartacea?: boolean; residente?: boolean }): PortalProfile {
  return {
    portal,
    sections: [
      {
        title: 'Intestatario',
        fields: [
          ...intestatario({ aziende: opts.aziende ?? false, forma: opts.forma ?? false }),
          sesso(opts.sessoReq ?? false, o(['NB', 'Non binario'])), ...nascita(),
          ...(opts.residente ? [check('DOMESTICO_RESIDENTE', 'Domestico residente')] : []),
          ...indirizzo('CLIENTE', true),
          ...contatti({ mobileReq: true, emailReq: opts.emailReq ?? true }),
          ...documento({ luogo: true }),
        ],
      },
      fornitura({ ...(opts.regioni ? { regioni: opts.regioni } : {}), ...(opts.regione ? { regione: true } : {}) }),
      fatturazione({ ...(opts.cartacea === undefined ? {} : { cartacea: opts.cartacea }), ...(opts.indirizzoFatturaReq ? { indirizzoReq: true } : {}) }),
      pagamento(opts.metodi),
      consensi(['AUTORIZZAZIONE_RIPENSAMENTO', 'CONSENSO_MARKETING', 'CONSENSO_TERZI', 'CONSENSO_PROFILAZIONE'], { vendita: true }),
    ],
  }
}
export const PROFILE_ACEA = coralyEnergia('Coraly · Acea', { regione: true, metodi: ['SDD'], sessoReq: true, cartacea: false })
export const PROFILE_A2A = coralyEnergia('Coraly · A2A', {
  regioni: ['SICILIA', 'SARDEGNA', 'LAZIO', 'CAMPANIA', 'FUORI REGIONE'], metodi: ['BOLLETTINO', 'SDD', 'CARTA'], residente: true,
})
export const PROFILE_ENEGA = coralyEnergia('Coraly · Enega', {
  aziende: true, forma: true, metodi: ['BOLLETTINO', 'SDD', 'CARTA'], emailReq: false, indirizzoFatturaReq: true,
})

/** Coraly, Enel Agent (Sales Process): residenziale o business, con delega. */
export const PROFILE_ENEL: PortalProfile = {
  portal: 'Coraly · Enel',
  sections: [
    {
      title: 'Intestatario',
      fields: [
        ...intestatario({ forma: true }), ...nascita(), check('DOMESTICO_RESIDENTE', 'Domestico residente'),
        ...indirizzo('CLIENTE', true), ...contatti({ mobileReq: true }),
        ...documento({ scadenza: false }),
        text('REGIONE_VENDITA', 'Regione di vendita', R),
      ],
    },
    fornitura(),
    fatturazione(),
    pagamento(['BOLLETTINO', 'SDD']),
    consensi(['AUTORIZZAZIONE_RIPENSAMENTO', 'CONSENSO_MARKETING', 'CONSENSO_TERZI', 'CONSENSO_PROFILAZIONE'], { codice: true, vendita: true }),
    delega(['Marito/Moglie', 'Convivente', 'Genitore', 'Figlio', 'Delegato']),
  ],
}

/** Profilo del portale per l'operatore (nome normalizzato), se ne ha uno dedicato. */
export function portalProfileFor(operatorName: string): PortalProfile | null {
  const n = operatorName.toUpperCase().replace(/[\s_.-]/g, '')
  if (n.includes('FASTWEB')) return PROFILE_FASTWEB
  if (n.includes('WIND')) return PROFILE_WIND3
  if (n.includes('EOLO')) return PROFILE_EOLO
  if (n.includes('VODAFONE')) return PROFILE_VODAFONE
  if (n.includes('TIM') && !n.includes('TIMBR')) return PROFILE_TIM
  if (n.includes('SKY')) return PROFILE_SKY
  if (n.includes('RETELIT')) return PROFILE_RETELIT
  if (n.includes('ACEA')) return PROFILE_ACEA
  if (n.includes('A2A')) return PROFILE_A2A
  if (n.includes('ENEGA')) return PROFILE_ENEGA
  if (n.includes('ENEL')) return PROFILE_ENEL
  return null
}

/** Campi visibili del profilo per i valori attuali. */
export function visibleFields(p: PortalProfile, v: Values, tipo: string): { section: ProfileSection; fields: ProfileField[] }[] {
  const val = { ...(p.defaults ?? {}), ...v }
  return p.sections
    .filter(s => !s.when || s.when(val, tipo))
    .map(section => ({ section, fields: section.fields.filter(f => !f.when || f.when(val, tipo)) }))
    .filter(s => s.fields.length > 0)
}

/**
 * Valori proposti dai campi con `defaultValue` (es. la data di oggi) per le chiavi ancora vuote: la scheda «Dati per il
 * portale» li mette nella bozza quando si entra in «Modifica dati», così si vedono e si salvano con Salva.
 */
export function dynamicProfileDefaults(p: PortalProfile, v: Values): Values {
  const out: Values = {}
  for (const s of p.sections)
    for (const f of s.fields)
      if (f.defaultValue && !(v[f.key] ?? '').trim() && !(f.key in out)) out[f.key] = f.defaultValue()
  return out
}

/** Etichette dei campi obbligatori (visibili) ancora vuoti. */
export function missingProfileFields(p: PortalProfile, v: Values, tipo: string): string[] {
  const val = { ...(p.defaults ?? {}), ...v }
  const miss: string[] = []
  for (const { fields } of visibleFields(p, v, tipo)) {
    for (const f of fields) {
      const req = typeof f.required === 'function' ? f.required(val, tipo) : !!f.required
      if (req && !(val[f.key] ?? '').trim()) miss.push(f.label)
    }
  }
  return miss
}
